import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import fastMemoize from 'fast-memoize';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList, deleteNote } from 'services/annotations/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import { debounce, get } from 'lodash';
import helpers from 'helpers';

import AdminTableBaseComponent from 'containers/tables/AdminTableBaseComponent';
import DeleteIconButton from 'components/molecules/buttons/DeleteNoteButton';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);
const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

class AnnotationsTableContainer extends AdminTableBaseComponent {
    constructor(props) {
        super(props);
        this.getColumns = this.getColumns.bind(this);
        this.activeSearch = false;
        this.state = {
            ...AnnotationsTableContainer.convertUriToState(props.parsedUrl)
        };

        this.annotationTableInstance = React.createRef();
        this.mirrorStateToUri = debounce(this.mirrorStateToUri.bind(this), 500);
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    getColumns(isEntitySpecific) {
        const columnConfig = [];

        if (!isEntitySpecific) {
            columnConfig.push({
                Header: 'ENTITY NAME',
                id: 'entityName',
                Cell: props => this.linkComponent({
                    toPath: `/admin/entities/${get(props, 'original.meta.entity.id')}`,
                    cellValue: get(props, 'original.meta.entity.name')
                }),
                accessor: d => get(d, 'meta.entity.name'),
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control entityName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Entity Name"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            });
        }

        return columnConfig.concat([{
            Header: 'CONTENT',
            accessor: 'meta.content',
            Cell: props => this.linkComponent({
                toPath: `/admin/annotations/${props.original.id}`,
                cellValue: get(props, 'original.meta.content')
            }),
            maxWidth: 700,
            filterable: true,
            Filter: ({ filter, onChange }) => (
                <DebouncedInput
                    type="text"
                    className="filter text-filter form-control content-filter"
                    onChange={value => onChange(value)}
                    onFocus={() => this.setActiveSearch(true)}
                    onBlur={() => this.setActiveSearch(false)}
                    placeholder="Content"
                    style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                    value={filter ? filter.value : ''} />
            ),
            filterMethod: (filter, row) => {
                const filterValue = memoizeFilterValue(filter, row);

                return filterValue.toLowerCase().includes(filter.value.toLowerCase());
            }
        }, {
            Header: 'CHANNEL',
            accessor: 'meta.dashboard',
            Cell: props => <CellDiv value={helpers.convertToDisplayValue(get(props, 'original.meta.dashboard'))} style={{ textAlign: 'left' }} />,
            maxWidth: 200,
            filterable: true,
            Filter: ({ filter, onChange }) => (
                <DebouncedInput
                    type="text"
                    className="filter text-filter form-control content-filter"
                    onChange={value => onChange(value)}
                    onFocus={() => this.setActiveSearch(true)}
                    onBlur={() => this.setActiveSearch(false)}
                    placeholder="Channel"
                    style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                    value={filter ? filter.value : ''} />
            ),
            filterMethod: (filter, row) => {
                const filterValue = memoizeFilterValue(filter, row);

                return filterValue.toLowerCase().includes(filter.value.toLowerCase());
            }
        }, {
            Header: 'USER',
            id: 'userEmail',
            Cell: props => this.linkComponent({
                toPath: `/admin/users/${get(props, 'original.meta.user.id')}/edit`,
                cellValue: get(props, 'original.meta.user.email')
            }),
            accessor: d => get(d, 'meta.user.email'),
            filterable: true,
            Filter: ({ filter, onChange }) => (
                <DebouncedInput
                    type="text"
                    className="filter text-filter form-control userName-filter"
                    onChange={value => onChange(value)}
                    onFocus={() => this.setActiveSearch(true)}
                    onBlur={() => this.setActiveSearch(false)}
                    placeholder="User Email"
                    style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                    value={filter ? filter.value : ''} />
            ),
            filterMethod: (filter, row) => {
                const filterValue = memoizeFilterValue(filter, row);

                return filterValue.toLowerCase().includes(filter.value.toLowerCase());
            }
        }, {
            Header: 'CREATED',
            accessor: 'createdAt',
            Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
            maxWidth: 150
        }, {
            Header: '',
            Cell: props => (
                <React.Fragment>
                    {this.deleteAnnotationButton(null, props.original)}
                </React.Fragment>
            ),
            maxWidth: 60,
            resizable: false,
            sortable: false
        }
        ]);
    }

    componentDidMount() {
        this.props.fetchList();
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'annotations.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > 25) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...AnnotationsTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    deleteAnnotationButton(cell, row) {
        return (
            <DeleteIconButton
                className={'icon-trash'}
                position={'relative'}
                onClick={() => this.props.deleteNote({ annotationId: row.id, refreshList: true })}
            />
        );
    }

    annotationDashboardLink(cell, row) {
        return (
            <Link to={`/dashboard/${row.id}/summary`} target="_blank" title="Go to dashboard">
                <span className="icon-open" />
            </Link>
        );
    }

    getAnnotationTableInstance() {
        if (!this.annotationTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.annotationTableInstance;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getAnnotationTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = this.DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'entityName':
                if (filter.value) {
                    query.entityName = filter.value;
                }
                break;

            case 'userEmail':
                if (filter.value) {
                    query.userEmail = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== this.DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== this.DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.entityName) {
            filtered.push({ id: 'entityName', value: query.entityName });
        }

        if (query.userEmail) {
            filtered.push({ id: 'userEmail', value: query.userEmail });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir !== 'asc' });
        } else {
            sorted.push({ id: 'createdAt', desc: true });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = this.DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = this.DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const { isEntitySpecific, entityId } = this.props;
        let data = this.props.annotations.list;

        if (isEntitySpecific) {
            data = data.filter(annotation => get(annotation, 'meta.entity.id') === entityId);
        }

        const dataFetched = this.props.annotations.listFetched;

        return (
            <Grid fluid>
                <br />
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.annotationTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns(isEntitySpecific)}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (

                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    entities: state.entities,
    annotations: state.annotations,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, deleteNote, push })(AnnotationsTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/AnnotationsTableContainer.js